import type {
    CreditNoteDocument, CreditNoteSummary, CreditNoteFreshness,
    CreditNoteListResult, CreditNoteLinesResult, CreditNoteFilters,
    CreditNoteReasonCatalogEntry, CreditNoteVocabularies,
    CreditNoteDetail, CreditNoteRecourseQueue, CreditNoteRecourseFilters,
    ReasonWritePayload, ReviewWritePayload, RecourseWritePayload,
} from '../types/creditNotes';

interface ListParams {
    page: number;
    limit: number;
    filters: CreditNoteFilters;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
}

/** Unwrap the house envelope, keeping the pagination total that `fetchApi` drops. */
async function unwrap<T>(res: Response): Promise<{ data: T; total: number }> {
    const body = await res.json();
    if (!res.ok || !body.success) {
        throw new Error(body.error?.message || `API error: ${res.status}`);
    }
    return { data: body.data as T, total: body.pagination?.total ?? 0 };
}

export const creditNotesApi = {
    /**
     * The list, its tiles and its exclusions in one call.
     *
     * `rule` is passed straight through. The tile numbers and this filter are computed from the
     * same evaluation server-side, which is the whole reason a tile may be clicked at all: what
     * the tile counts is what the filter returns. Never re-derive either of them here.
     */
    async list(params: ListParams): Promise<CreditNoteListResult> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);
        for (const [key, value] of Object.entries(params.filters)) {
            if (value === undefined || value === null || value === '') continue;
            sp.set(key, String(value));
        }

        const res = await fetch(`/api/v1/credit-notes?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data, total } = await unwrap<{
            documents: CreditNoteDocument[];
            summary: CreditNoteSummary;
            freshness: CreditNoteFreshness;
            availableCompanies: string[];
            reasonCatalog: CreditNoteReasonCatalogEntry[];
            vocabularies: CreditNoteVocabularies;
        }>(res);

        return {
            documents: data?.documents ?? [],
            summary: data.summary,
            freshness: data.freshness,
            availableCompanies: data?.availableCompanies ?? [],
            total,
            reasonCatalog: data?.reasonCatalog ?? [],
            vocabularies: data.vocabularies,
        };
    },

    /**
     * One document's own ERP lines — the item lines and the free-text comment lines.
     *
     * BOTH IDENTIFIERS GO IN THE QUERY STRING. A company code cannot be a URL path segment on
     * this host: `bin` is an IIS hidden segment, so `/api/v1/credit-notes/bin/...` returns a
     * plain HTML 404 from the gateway before the function runs — for one of the two companies
     * this page was built for. See the endpoint's own header for the measurement.
     */
    async lines(companyCode: string, documentNo: string): Promise<CreditNoteLinesResult> {
        const sp = new URLSearchParams({ company: companyCode, documentNo });
        const res = await fetch(
            `/api/v1/credit-notes/lines?${sp}`,
            { headers: { 'Content-Type': 'application/json' } },
        );
        const { data } = await unwrap<CreditNoteLinesResult>(res);
        return data;
    },

    /**
     * Everything the APP knows about one document: the reason, the two status tracks, the
     * recourse cases, the audit trail and the vocabularies the forms are built from.
     *
     * Both identifiers in the query string, for the same reason as `lines` — `bin` is an IIS
     * hidden segment and can never be a path segment on this host.
     */
    async detail(companyCode: string, documentNo: string): Promise<CreditNoteDetail> {
        const sp = new URLSearchParams({ company: companyCode, documentNo });
        const res = await fetch(`/api/v1/credit-notes/detail?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data } = await unwrap<CreditNoteDetail>(res);
        return data;
    },

    /**
     * The three write paths.
     *
     * PUT, and the company and document number go in the BODY — never in the path. Each returns
     * the WHOLE document state, so the drawer refreshes the reason, both tracks and the trail
     * from the one response. A response carrying only the edited field would leave the panels
     * beside it showing what the database no longer holds, and a stale panel next to a fresh one
     * is the harder bug to notice: the screen is wrong while the database is right.
     */
    async saveReason(payload: ReasonWritePayload): Promise<CreditNoteDetail> {
        return writeState('reason', payload);
    },

    async saveReview(payload: ReviewWritePayload): Promise<CreditNoteDetail> {
        return writeState('review', payload);
    },

    async saveRecourse(payload: RecourseWritePayload): Promise<CreditNoteDetail> {
        return writeState('recourse', payload);
    },

    /**
     * Write the credit note number onto the complaint ticket named by its recourse case.
     *
     * The ticket id is NOT sent: the server reads it from `CreditNoteRecourse.HubSpotTicketId`,
     * which is where the plan puts it. Sending it from here would create a second place the
     * reference can be typed, and therefore a way for the two to disagree.
     */
    async writeHubSpotTicket(payload: { company: string; documentNo: string }): Promise<{
        ticketId: string; written: boolean; alreadySet: boolean;
        creditNoteNo: string; property: string; ticketSubject: string | null; message: string;
    }> {
        const res = await fetch('/api/v1/credit-notes/hubspot-ticket', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const body = await res.json();
        if (!res.ok || !body.success) {
            throw new Error(body.error?.message || `The write failed (status ${res.status}).`);
        }
        return body.data;
    },

    /**
     * The Excel export of the CURRENTLY FILTERED list, plus the trend figures on a second sheet.
     *
     * The same query string the list was fetched with goes to the server, which runs the SAME
     * filter function — so the file is the filtered set by construction. Nothing is filtered
     * here: a browser-side copy of the predicates is exactly how an export starts disagreeing
     * with the table it came from.
     */
    async export(filters: CreditNoteFilters): Promise<void> {
        const sp = new URLSearchParams();
        for (const [key, value] of Object.entries(filters)) {
            if (value === undefined || value === null || value === '') continue;
            sp.set(key, String(value));
        }
        const res = await fetch(`/api/v1/credit-notes/export?${sp}`);
        if (res.status === 401) {
            const { redirectToLogin } = await import('../utils/auth-redirect');
            redirectToLogin();
            throw new Error('Session expired');
        }
        if (!res.ok) {
            let message = `Export failed (status ${res.status})`;
            try {
                const body = await res.json() as { error?: { message?: string } };
                if (body.error?.message) message = body.error.message;
            } catch { /* an HTML error page, not the house envelope */ }
            throw new Error(message);
        }
        const disposition = res.headers.get('content-disposition') || '';
        const match = /filename="?([^"]+)"?/.exec(disposition);
        const filename = match?.[1] || 'CreditNotes.xlsx';
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
    },

    /**
     * The recourse queue.
     *
     * NOT SCOPED BY THE LIST'S PERIOD. A claim does not stop being open because a reader
     * narrowed a date filter, and the KPI on the credit-notes tab counts the same set through
     * the same server-side function.
     */
    async recourseQueue(filters: CreditNoteRecourseFilters): Promise<CreditNoteRecourseQueue> {
        const sp = new URLSearchParams();
        if (filters.search) sp.set('search', filters.search);
        if (filters.status) sp.set('status', filters.status);
        if (filters.counterpartyType) sp.set('counterpartyType', filters.counterpartyType);
        if (filters.company) sp.set('company', filters.company);
        if (filters.overdueOnly) sp.set('overdueOnly', 'true');
        if (filters.openOnly) sp.set('openOnly', 'true');

        const res = await fetch(`/api/v1/credit-notes/recourse?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data } = await unwrap<CreditNoteRecourseQueue>(res);
        return data;
    },
};

async function writeState(
    resource: 'reason' | 'review' | 'recourse',
    payload: ReasonWritePayload | ReviewWritePayload | RecourseWritePayload,
): Promise<CreditNoteDetail> {
    const res = await fetch(`/api/v1/credit-notes/${resource}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const { data } = await unwrap<CreditNoteDetail>(res);
    return data;
}
